import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Trophy, Lock, Share2 } from 'lucide-react';
import { Button } from './ui/button';
import { BOSS_LORE, BOSS_ORDER } from '../game/lore';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Render a 1200×630 Twitter-card-sized PNG summarizing the player's boss
 * defeat progress and trigger a browser download. Uses an offscreen canvas
 * so it does not interfere with the game canvas or DOM state.
 */
function generateShareCard(defeatedSet, walletDisplay) {
  const W = 1200, H = 630;
  const canvas = document.createElement('canvas');
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext('2d');
  // Background gradient
  const grad = ctx.createLinearGradient(0, 0, W, H);
  grad.addColorStop(0, '#0a0a0f');
  grad.addColorStop(0.5, '#1a0a2e');
  grad.addColorStop(1, '#0f0a1a');
  ctx.fillStyle = grad; ctx.fillRect(0, 0, W, H);
  // Corner glow
  ctx.fillStyle = 'rgba(255,215,0,0.08)';
  ctx.beginPath(); ctx.arc(W, 0, 400, 0, Math.PI * 2); ctx.fill();
  ctx.fillStyle = 'rgba(153,69,255,0.08)';
  ctx.beginPath(); ctx.arc(0, H, 400, 0, Math.PI * 2); ctx.fill();
  // Title
  ctx.font = 'bold 56px monospace';
  ctx.fillStyle = '#FFD700';
  ctx.fillText('BOSS ROSTER', 60, 90);
  ctx.font = 'bold 24px monospace';
  ctx.fillStyle = '#4ADE80';
  ctx.fillText('METAL LEGO', 60, 125);
  // Progress line
  const total = BOSS_ORDER.length;
  const defeated = BOSS_ORDER.filter(k => defeatedSet.has(k)).length;
  ctx.font = 'bold 42px monospace';
  ctx.fillStyle = '#fff';
  ctx.fillText(`${defeated}/${total} DEFEATED`, 60, 195);
  // Grid of 20 boss slots — 5 cols × 4 rows, each slot 200×90
  const startX = 60, startY = 240, slotW = 210, slotH = 85, gapX = 14, gapY = 12;
  for (let i = 0; i < BOSS_ORDER.length; i++) {
    const bossKey = BOSS_ORDER[i];
    const boss = BOSS_LORE[bossKey];
    const col = i % 5, row = Math.floor(i / 5);
    const sx = startX + col * (slotW + gapX);
    const sy = startY + row * (slotH + gapY);
    const isDefeated = defeatedSet.has(bossKey);
    // Slot background
    ctx.fillStyle = isDefeated ? 'rgba(255,215,0,0.1)' : 'rgba(50,50,50,0.3)';
    ctx.fillRect(sx, sy, slotW, slotH);
    ctx.strokeStyle = isDefeated ? boss.color : '#333';
    ctx.lineWidth = 2;
    ctx.strokeRect(sx, sy, slotW, slotH);
    // Silhouette disc
    ctx.fillStyle = isDefeated ? boss.color : '#222';
    ctx.beginPath(); ctx.arc(sx + 40, sy + slotH / 2, 26, 0, Math.PI * 2); ctx.fill();
    if (isDefeated) {
      ctx.fillStyle = '#000';
      ctx.font = 'bold 32px sans-serif';
      ctx.fillText('★', sx + 30, sy + slotH / 2 + 12);
    } else {
      ctx.fillStyle = '#666';
      ctx.font = 'bold 28px sans-serif';
      ctx.fillText('?', sx + 34, sy + slotH / 2 + 10);
    }
    // Boss name (or ??? if locked)
    ctx.font = 'bold 15px monospace';
    ctx.fillStyle = isDefeated ? '#fff' : '#555';
    ctx.fillText(isDefeated ? boss.name : '???', sx + 80, sy + 38);
    // Tier + level
    ctx.font = '12px monospace';
    ctx.fillStyle = isDefeated ? boss.color : '#444';
    ctx.fillText(`${boss.tier} · L${boss.levels[0]}`, sx + 80, sy + 60);
  }
  // Footer with wallet + URL
  ctx.font = '16px monospace';
  ctx.fillStyle = '#888';
  ctx.fillText(walletDisplay || 'GUEST', 60, H - 30);
  ctx.fillStyle = '#14F195';
  ctx.font = 'bold 16px monospace';
  ctx.fillText('footstabberml.com', W - 260, H - 30);
  return canvas;
}

async function downloadShareCard(canvas, filename) {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      if (!blob) { resolve(false); return; }
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      resolve(true);
    }, 'image/png');
  });
}

/**
 * Boss Roster Gallery
 *
 * Grid of the 6 boss visuals. Locked bosses show a silhouette + "???" + the
 * levels they gate. Unlocked bosses show the full name, tier chip, description,
 * weakness tip, and the "First Defeated" level (min level in their `levels`
 * array — a cheap proxy since we don't yet track the exact defeat timestamp).
 */
const BossRoster = ({ walletAddress, onBack }) => {
  const [bestiary, setBestiary] = useState({ defeated_boss_types: [] });
  const [loading, setLoading] = useState(true);
  const [sharing, setSharing] = useState(false);

  useEffect(() => {
    let ident = walletAddress;
    if (!ident) {
      ident = sessionStorage.getItem('metal_lego_guest_id');
    }
    if (!ident) { setLoading(false); return; }
    axios.get(`${API}/player/bestiary/${ident}`)
      .then(res => setBestiary(res.data))
      .catch(err => console.error('[BossRoster] fetch failed:', err))
      .finally(() => setLoading(false));
  }, [walletAddress]);

  const defeatedSet = new Set(bestiary.defeated_boss_types || []);

  const handleShare = async () => {
    setSharing(true);
    try {
      const walletDisplay = walletAddress
        ? `${walletAddress.slice(0, 4)}...${walletAddress.slice(-4)}`
        : 'GUEST';
      const canvas = generateShareCard(defeatedSet, walletDisplay);
      const ok = await downloadShareCard(canvas, `metal-lego-boss-roster.png`);
      if (ok) toast.success('Share card downloaded — post it wherever!');
      else toast.error('Share card export failed');
    } catch (e) {
      console.error('[BossRoster] share failed:', e);
      toast.error('Could not generate share card');
    } finally {
      setSharing(false);
    }
  };

  return (
    <div data-testid="boss-roster-screen" className="min-h-screen bg-[#0a0a0a] text-white p-4 sm:p-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8 gap-3 flex-wrap">
          <div>
            <h1 className="font-game text-4xl sm:text-5xl text-[#FFD700] flex items-center gap-3">
              <Trophy className="w-10 h-10" /> BOSS ROSTER
            </h1>
            <p className="text-sm text-gray-400 mt-2">
              {bestiary.unique_bosses || 0} of {BOSS_ORDER.length} bosses defeated
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              data-testid="boss-roster-share-btn"
              onClick={handleShare}
              disabled={sharing}
              className="bg-gradient-to-r from-[#FFD700] to-[#FF9500] text-black hover:brightness-110 border border-[#FFD700]"
            >
              <Share2 className="w-4 h-4 mr-2" />
              {sharing ? 'Generating…' : 'SHARE'}
            </Button>
            <Button
              data-testid="boss-roster-back-btn"
              onClick={onBack}
              variant="outline"
              className="border-[#14F195] text-[#14F195] hover:bg-[#14F195]/10"
            >
              ← BACK
            </Button>
          </div>
        </div>

        {loading ? (
          <p className="text-center text-gray-500 py-16">Loading roster…</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {BOSS_ORDER.map((bossType) => {
              const boss = BOSS_LORE[bossType];
              const isDefeated = defeatedSet.has(bossType);
              return (
                <div
                  key={bossType}
                  data-testid={`boss-card-${bossType}`}
                  className={`relative rounded-lg border-2 p-6 transition-all ${
                    isDefeated
                      ? 'border-[#FFD700] bg-gradient-to-br from-[#1a1a2e] to-[#2a1a1a] shadow-[0_0_20px_rgba(255,215,0,0.15)]'
                      : 'border-gray-700 bg-[#111] opacity-70'
                  }`}
                >
                  {/* Silhouette placeholder — colored disc keyed to boss.color */}
                  <div
                    className="w-28 h-28 mx-auto mb-4 rounded-full flex items-center justify-center relative overflow-hidden"
                    style={{
                      background: isDefeated
                        ? `radial-gradient(circle, ${boss.color}66, ${boss.color}00)`
                        : '#222',
                      border: `2px solid ${isDefeated ? boss.color : '#444'}`,
                      boxShadow: isDefeated ? `0 0 30px ${boss.color}66` : 'none',
                    }}
                  >
                    {isDefeated ? (
                      <span className="font-game text-4xl" style={{ color: boss.color, textShadow: `0 0 12px ${boss.color}` }}>
                        ★
                      </span>
                    ) : (
                      <Lock className="w-10 h-10 text-gray-600" />
                    )}
                  </div>

                  <div className="flex items-center justify-between mb-2">
                    <h2 className="font-game text-base text-white" data-testid={`boss-name-${bossType}`}>
                      {isDefeated ? boss.name : '??? ??? ???'}
                    </h2>
                    <span
                      className="text-xs px-2 py-1 rounded-full font-bold"
                      style={{
                        background: isDefeated ? `${boss.color}22` : '#222',
                        color: isDefeated ? boss.color : '#666',
                        border: `1px solid ${isDefeated ? boss.color : '#333'}`,
                      }}
                    >
                      {boss.tier}
                    </span>
                  </div>

                  <p className="text-xs text-gray-500 mb-3">
                    Encountered on levels: <span className="text-gray-300">{boss.levels.join(', ')}</span>
                  </p>

                  {isDefeated ? (
                    <>
                      <p className="text-sm text-gray-300 leading-relaxed mb-3">
                        {boss.description}
                      </p>
                      <div className="border-l-2 pl-3 py-1" style={{ borderColor: boss.color }}>
                        <p className="text-xs text-gray-400 italic">
                          <span className="font-bold text-gray-200">Weakness:</span> {boss.weakness}
                        </p>
                      </div>
                    </>
                  ) : (
                    <p className="text-sm text-gray-500 italic">
                      Reach level {Math.min(...boss.levels)} and defeat this boss to unlock lore.
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default BossRoster;
